import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  X,
  Search,
  ShoppingCart,
  Receipt,
  Plus,
  Minus,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  UserRound,
  Hash,
} from 'lucide-react';
import { exchangesAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import { CurrencyInput, ItemSearchComponent, ItemListComponent, PercentageInput, CheckoutInfoModal } from '../ui';
import WebOrderLookup from '../ui/WebOrderLookup';
import dayjs from '../../utils/dayjs';
import { useDiscountApproval } from '../../contexts/DiscountApprovalContext';
import { printExchangeReceipt } from '../../utils/pdf';

const round2 = (n) => Math.round(n * 100) / 100;

// One side's discount: a % of its total, or a Rs amount typed directly (capped
// at the total). Whichever was typed, the other figure is derived from it so
// the two boxes always agree.
const resolveDiscount = (total, percentage, fixedAmount) => {
  if (fixedAmount === null) {
    return { amount: (total * percentage) / 100, percentage };
  }
  const amount = Math.min(fixedAmount, total);
  return { amount, percentage: total > 0 ? round2((amount / total) * 100) : 0 };
};

const ExchangeForm = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customerInfoOpen, setCustomerInfoOpen] = useState(false);
  const customerInfoRef = useRef(null);
  const { discountApprovalFlowLauncher } = useDiscountApproval()

  // Close customer-info popover when clicking outside
  useEffect(() => {
    if (!customerInfoOpen) return;
    const handleClick = (e) => {
      if (customerInfoRef.current && !customerInfoRef.current.contains(e.target)) {
        setCustomerInfoOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [customerInfoOpen]);



  // Exchange form state
  const [formData, setFormData] = useState({
    ExchangeNo: '',
    ExchangeRemarks: '',
    CustomerName: '',
    CustomerPhone: '',
    CashReceived: 0,
    CashReturned: 0,
    BankReceived: 0,
    BankReturned: 0,
    Discount: 0,
    Remarks: '',
  });

  // Exchange items state
  const [returnedItems, setReturnedItems] = useState([]);
  const [givenItems, setGivenItems] = useState([]);

  // Separate discount state for returned and given items
  const [returnedItemsDiscountPercentage, setReturnedItemsDiscountPercentage] = useState(0);
  const [givenItemsDiscountPercentage, setGivenItemsDiscountPercentage] = useState(0);
  // A Rs amount typed straight into "Discount Rs". While set it is the discount
  // and the % is only derived from it for display; null = the % drives. Needed
  // because a 2-decimal % can't always hit an exact figure (e.g. an even
  // exchange: 76.15% of 13,500 is 10,280.25, not the 10,280 required).
  const [returnedItemsDiscountFixed, setReturnedItemsDiscountFixed] = useState(null);
  const [givenItemsDiscountFixed, setGivenItemsDiscountFixed] = useState(null);

  // Web order mode state
  const [returnMode, setReturnMode] = useState('in-store'); // 'in-store' | 'web-order'
  const [fetchedOrderRefs, setFetchedOrderRefs] = useState([]); // [{brand, woocommerce_order_id}]

  // Checkout modal state
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [bankReceived, setBankReceived] = useState('');


  useEffect(() => {
    generateExchangeNumber();
  }, []);

  // Keyboard shortcut handler for Ctrl+Enter to open checkout modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        if (!saving && !checkoutOpen && (returnedItems.length > 0 || givenItems.length > 0)) {
          handleOpenCheckout();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [saving, checkoutOpen, returnedItems.length, givenItems.length]);




  const generateExchangeNumber = () => {
    const now = dayjs();
    const exchangeNo = `EX-${now.format('YYYYMMDD')}-${now.format('HHmmss')}`;
    setFormData(prev => ({ ...prev, ExchangeNo: exchangeNo }));
  };






  const handleReturnedItemSelect = (item) => {
    // Check if item already exists in returned items
    const existingItemIndex = returnedItems.findIndex(ri => ri.Item_ID === item.ID);
    if (existingItemIndex !== -1) {
      // If item exists, increment quantity and move to top
      setReturnedItems(prev => {
        const updatedItems = [...prev];
        const [movedItem] = updatedItems.splice(existingItemIndex, 1);
        const updatedMovedItem = { ...movedItem, quantity: movedItem.quantity + 1 };
        return [updatedMovedItem, ...updatedItems];
      });
    } else {
      // If item doesn't exist, add it to the top
      const newItem = {
        Item_ID: item.ID,
        item: item,
        quantity: 1,
        price: parseFloat(item.SalePrice || item.Price || 0),
        discount: parseFloat((item.Price || 0 )- (item.SalePrice || 0)),
        actualPrice: parseFloat(item.Price || 0),
      };
      setReturnedItems(prev => [newItem, ...prev]);
    }
  };

  const handleGivenItemSelect = (item) => {
    // Check if item already exists in Sold Items
    const existingItemIndex = givenItems.findIndex(gi => gi.Item_ID === item.ID);
    if (existingItemIndex !== -1) {
      // If item exists, increment quantity and move to top
      setGivenItems(prev => {
        const updatedItems = [...prev];
        const [movedItem] = updatedItems.splice(existingItemIndex, 1);
        const updatedMovedItem = { ...movedItem, quantity: movedItem.quantity + 1 };
        return [updatedMovedItem, ...updatedItems];
      });
    } else {
      // If item doesn't exist, add it to the top
      const newItem = {
        Item_ID: item.ID,
        item: item,
        quantity: 1,
        price: parseFloat(item.SalePrice || item.Price || 0),
        discount: parseFloat(item.Price - item.SalePrice || 0),
        actualPrice: parseFloat(item.Price || 0),
      };
      setGivenItems(prev => [newItem, ...prev]);
    }
  };


  // Quantity handlers for ItemListComponent
  const handleReturnedItemQuantityIncrement = (itemId) => {
    setReturnedItems(prev => {
      const itemIndex = prev.findIndex(item => item.Item_ID === itemId);
      if (itemIndex !== -1) {
        const updatedItems = [...prev];
        const [movedItem] = updatedItems.splice(itemIndex, 1);
        // Respect maxQuantity for web order items
        if (movedItem.maxQuantity && movedItem.quantity >= movedItem.maxQuantity) {
          return prev;
        }
        const updatedMovedItem = { ...movedItem, quantity: movedItem.quantity + 1 };
        return [updatedMovedItem, ...updatedItems];
      }
      return prev;
    });
  };

  const handleReturnedItemQuantityDecrement = (itemId) => {
    setReturnedItems(prev => 
        prev.map(item => 
          item.Item_ID === itemId 
          ? { ...item, quantity: Math.max(1, item.quantity - 1) }
            : item
        )
      );
  };

  const handleGivenItemQuantityIncrement = (itemId) => {
    setGivenItems(prev => {
      const itemIndex = prev.findIndex(item => item.Item_ID === itemId);
      if (itemIndex !== -1) {
        const updatedItems = [...prev];
        const [movedItem] = updatedItems.splice(itemIndex, 1);
        const updatedMovedItem = { ...movedItem, quantity: movedItem.quantity + 1 };
        return [updatedMovedItem, ...updatedItems];
      }
      return prev;
    });
  };

  const handleGivenItemQuantityDecrement = (itemId) => {
      setGivenItems(prev => 
        prev.map(item => 
          item.Item_ID === itemId 
          ? { ...item, quantity: Math.max(1, item.quantity - 1) }
            : item
        )
      );
  };


  // Staff can change the unit price of a returned item (the customer paid full price and the
  // item is now on sale) or a sold one (a per-item discount). Price is saved as
  // max(catalogue price, new price) with the difference as discount, so Price - Discount
  // always equals the unit price actually charged or refunded.
  const repriceItem = (item, newPrice) => {
    const listPrice = parseFloat(item.item?.Price || 0);
    const actualPrice = Math.max(listPrice, newPrice);
    return { ...item, price: newPrice, actualPrice, discount: round2(actualPrice - newPrice) };
  };

  const handleReturnedItemPriceChange = (itemId, newPrice) => {
    setReturnedItems(prev => prev.map(item => (item.Item_ID === itemId ? repriceItem(item, newPrice) : item)));
  };

  const handleGivenItemPriceChange = (itemId, newPrice) => {
    setGivenItems(prev => prev.map(item => (item.Item_ID === itemId ? repriceItem(item, newPrice) : item)));
  };

  // In-store returns only: web-order returns are refunded at what the website charged.
  const createEditableUnitPrices = (items) => {
    return items.reduce((acc, item) => {
      if (!item.webOrderRef) acc[item.Item_ID] = item.price;
      return acc;
    }, {});
  };

  const removeItem = (itemId, type) => {
    if (type === 'returned') {
      setReturnedItems(prev => prev.filter(item => item.Item_ID !== itemId));
    } else {
      setGivenItems(prev => prev.filter(item => item.Item_ID !== itemId));
    }
  };

  // Helper function to create item quantities object for ItemListComponent
  const createItemQuantities = (items) => {
    return items.reduce((acc, item) => {
      acc[item.Item_ID] = item.quantity;
      return acc;
    }, {});
  };

  // Helper function to convert items to the format expected by ItemListComponent
  const convertItemsForList = (items) => {
    return items.map(item => {
      const base = item.item || item;
      // Preserve maxQuantity from web order items
      if (item.maxQuantity !== undefined) {
        return { ...base, maxQuantity: item.maxQuantity };
      }
      return base;
    });
  };


  // Separate discount handlers. Both inputs re-send their current value when
  // they lose focus, so a value equal to what is already shown is not an edit —
  // without that check, tabbing through the % box would turn a typed Rs amount
  // back into its rounded %.
  const handleReturnedItemsDiscountPercentageChange = (percentage) => {
    const newPercentage = Number(percentage) || 0;
    if (
      returnedItemsDiscountFixed !== null &&
      newPercentage === calculateTotals().returnedItemsDiscountPercentage
    ) return;
    setReturnedItemsDiscountPercentage(newPercentage);
    setReturnedItemsDiscountFixed(null);
  };

  const handleGivenItemsDiscountPercentageChange = (percentage) => {
    const newPercentage = Number(percentage) || 0;
    if (
      givenItemsDiscountFixed !== null &&
      newPercentage === calculateTotals().givenItemsDiscountPercentage
    ) return;
    setGivenItemsDiscountPercentage(newPercentage);
    setGivenItemsDiscountFixed(null);
  };

  const handleReturnedItemsDiscountAmountChange = (amount) => {
    const newAmount = Number(amount) || 0;
    if (newAmount === round2(calculateTotals().returnedDiscountAmount)) return;
    setReturnedItemsDiscountFixed(newAmount > 0 ? newAmount : null);
    setReturnedItemsDiscountPercentage(0);
  };

  const handleGivenItemsDiscountAmountChange = (amount) => {
    const newAmount = Number(amount) || 0;
    if (newAmount === round2(calculateTotals().givenDiscountAmount)) return;
    setGivenItemsDiscountFixed(newAmount > 0 ? newAmount : null);
    setGivenItemsDiscountPercentage(0);
  };

  const clearReturnedItemsDiscount = () => {
    setReturnedItemsDiscountPercentage(0);
    setReturnedItemsDiscountFixed(null);
  };

  const clearGivenItemsDiscount = () => {
    setGivenItemsDiscountPercentage(0);
    setGivenItemsDiscountFixed(null);
  };

  // Checkout modal handlers
  const handleOpenCheckout = async() => {
    const t = calculateTotals();
    // A sold item's discount splits into the catalogue sale discount (no approval needed)
    // and whatever staff cut below the POS price by editing it, which counts toward the
    // approval limit together with the Discount %/Rs on the sold side.
    const givenLines = givenItems.map((gi) => {
      const quantity = Number(gi.quantity || 0);
      const posPrice = parseFloat(gi.item?.SalePrice || gi.item?.Price || 0);
      const price = Number(gi.price || 0);
      const unitPrice = Number(gi.actualPrice || gi.price || 0);
      return {
        gi,
        quantity,
        posPrice,
        unitPrice,
        catalogueDiscount: Math.max(unitPrice - Math.max(price, posPrice), 0),
        priceCut: Math.max(posPrice - price, 0),
      };
    });
    const givenPosTotal = givenLines.reduce((s, l) => s + l.quantity * l.posPrice, 0);
    const givenPriceCuts = givenLines.reduce((s, l) => s + l.quantity * l.priceCut, 0);
    const requestedDiscount = givenPriceCuts + Number(t.givenDiscountAmount || 0);
    const approvalPercentage = givenPosTotal > 0 ? round2((requestedDiscount / givenPosTotal) * 100) : 0;

    // Build a display-only summary of the given (sold) items for the approval email
    const saleSummary = {
      items: givenLines.map((l) => ({
        name: l.gi.item?.Name || l.gi.item?.Description || 'Item',
        quantity: l.quantity,
        unit_price: l.unitPrice,
        line_total: l.quantity * l.unitPrice,
        item_discount: l.quantity * l.catalogueDiscount,
      })),
      subtotal: givenLines.reduce((s, l) => s + l.quantity * l.unitPrice, 0),
      item_discount_total: givenLines.reduce((s, l) => s + l.quantity * l.catalogueDiscount, 0),
      requested_discount_amount: requestedDiscount,
      net_total: Number(t.netGivenTotal || 0),
      customer_name: formData.CustomerName || '',
      customer_phone: formData.CustomerPhone || '',
      exchange_difference: Number(t.exchangeDifference || 0),
    };

    const result = await discountApprovalFlowLauncher(
      // Effective % off the POS prices, so a discount typed in Rs or made by editing a
      // sold item's price is held to the same limit.
      approvalPercentage,
      saleSummary,
    );
    if(result) {
      // Reset cash and bank received values when opening checkout
      setCashReceived('');
      setBankReceived('');
      setCheckoutOpen(true);
    } else {
      addNotification({
        type: 'error',
        message: 'Discount rejected'
      });
    }
  };

  const handleCloseCheckout = () => {
    setCheckoutOpen(false);
  };

  const handleConfirmCheckout = async () => {
    setCheckoutOpen(false);
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const totals = calculateTotals();

      const submitData = {
        ...formData,
        ExchangeDate: new Date().toISOString(),
        TotalReturnValue: totals.returnedTotal,
        TotalGivenValue: totals.givenTotal,
        ExchangeDifference: totals.exchangeDifference,
        ReturnedItemsDiscountPercentage: totals.returnedItemsDiscountPercentage,
        GivenItemsDiscountPercentage: totals.givenItemsDiscountPercentage,
        ReturnedItemsDiscountAmount: totals.returnedDiscountAmount,
        GivenItemsDiscountAmount: totals.givenDiscountAmount,
        CashReceived: Number(cashReceived) || 0,
        BankReceived: Number(bankReceived) || 0,
        CashReturned: Math.max((Number(cashReceived) || 0) + (Number(bankReceived) || 0) - totals.exchangeDifference, 0),
        returned_items: returnedItems.map(item => ({
          Item_ID: item.Item_ID,
          quantity: item.quantity,
          price: item.price,
          discount: item.discount,
          actualPrice: item.actualPrice,
          exchange_type: 'returned'
        })),
        given_items: givenItems.map(item => ({
          Item_ID: item.Item_ID,
          quantity: item.quantity,
          price: item.price,
          actualPrice: item.actualPrice,
          discount: item.discount,
          exchange_type: 'given'
        })),
        web_order_refs: fetchedOrderRefs.length > 0 ? fetchedOrderRefs : undefined,
      };

      const response = await exchangesAPI.create(submitData);
      setSuccess('Exchange created successfully!');

      // Store exchange data for receipt printing
      const exchangeData = {
        ...response.data,
      };
      // Print exchange receipt
      await handleRenderExchangePDF(exchangeData);

      setTimeout(() => {
        navigate('/exchange');
      }, 1500);

    } catch (error) {
      console.error('Error saving exchange:', error);
      setError(error.response?.data?.detail || 'Failed to save exchange');
    } finally {
      setSaving(false);
    }
  };

  // Generate PDF receipt for exchange
  const handleRenderExchangePDF = async (exchangeData) => {
    if (!exchangeData) {
      console.error('No exchange data available for PDF generation');
      return;
    }

    try {
      // For now, we'll use the existing printSaleReceipt function
      // TODO: Create a dedicated printExchangeReceipt function
      await printExchangeReceipt(exchangeData);
      
    } catch (error) {
      console.error('Error generating exchange PDF:', error);
      alert('Error generating exchange receipt. Please try again.');
    }
  };

  const calculateTotals = () => {
    const returnedTotal = returnedItems.reduce((sum, item) => {
      return sum + (item.quantity * item.price);
    }, 0);

    const givenTotal = givenItems.reduce((sum, item) => {
      return sum + (item.quantity * item.price);
    }, 0);

    // Apply separate discounts to returned and given items
    const returnedDiscount = resolveDiscount(returnedTotal, returnedItemsDiscountPercentage, returnedItemsDiscountFixed);
    const givenDiscount = resolveDiscount(givenTotal, givenItemsDiscountPercentage, givenItemsDiscountFixed);
    const returnedDiscountAmount = returnedDiscount.amount;
    const givenDiscountAmount = givenDiscount.amount;

    const netReturnedTotal = returnedTotal - returnedDiscountAmount;
    const netGivenTotal = givenTotal - givenDiscountAmount;
    
    const exchangeDifference = netGivenTotal - netReturnedTotal;

    return {
      returnedTotal,
      givenTotal,
      netReturnedTotal,
      netGivenTotal,
      exchangeDifference,
      returnedItemsDiscountPercentage: returnedDiscount.percentage,
      givenItemsDiscountPercentage: givenDiscount.percentage,
      returnedDiscountAmount,
      givenDiscountAmount,
    };
  };

  const clearMessages = () => {
    if (error) setError('');
    if (success) setSuccess('');
  };


  const handleCancel = () => {
    navigate('/exchange');
  };


  const totals = calculateTotals();


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            New Exchange
          </h1>
          {formData.ExchangeNo && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-sm font-mono">
              <Hash className="h-3.5 w-3.5" />
              {formData.ExchangeNo}
            </span>
          )}
        </div>

        {/* Customer info popover trigger */}
        <div className="relative" ref={customerInfoRef}>
          <button
            type="button"
            onClick={() => setCustomerInfoOpen(prev => !prev)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              formData.CustomerName || formData.CustomerPhone
                ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <UserRound className="h-4 w-4" />
            <span className="text-sm">
              {formData.CustomerName || formData.CustomerPhone || 'Customer info'}
            </span>
          </button>

          {customerInfoOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 p-4 z-50">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={formData.CustomerName}
                    onChange={(e) => setFormData(prev => ({ ...prev, CustomerName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Phone
                  </label>
                  <input
                    type="text"
                    value={formData.CustomerPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, CustomerPhone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <p className="text-green-800">{success}</p>
        </div>
      )}

      <form id="exchange-form" className="space-y-6">
        {/* Exchange Summary - compact strip */}
        <div className="bg-white rounded-lg shadow px-6 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="text-gray-500 font-medium">Summary</span>
          <span className="flex items-center gap-1.5">
            <span className="text-gray-600">Net Returned:</span>
            <span className="font-semibold text-blue-600">{formatPrice(totals.netReturnedTotal)}</span>
          </span>
          <span className="text-gray-300">·</span>
          <span className="flex items-center gap-1.5">
            <span className="text-gray-600">Net Given:</span>
            <span className="font-semibold text-green-600">{formatPrice(totals.netGivenTotal)}</span>
          </span>
          <span className="text-gray-300">·</span>
          <span className="flex items-center gap-1.5">
            <span className="text-gray-600">
              {totals.exchangeDifference > 0
                ? 'Customer Pays:'
                : totals.exchangeDifference < 0
                ? 'Store Pays:'
                : 'Even Exchange:'}
            </span>
            <span className={`font-semibold ${
              totals.exchangeDifference > 0
                ? 'text-red-600'
                : totals.exchangeDifference < 0
                ? 'text-green-600'
                : 'text-gray-600'
            }`}>
              {formatPrice(Math.abs(totals.exchangeDifference))}
            </span>
          </span>
          <span className="text-gray-300">·</span>
          <span className="flex items-center gap-1.5">
            <span className="text-gray-600">Items:</span>
            <span className="font-semibold text-purple-600">{returnedItems.length + givenItems.length}</span>
          </span>
        </div>

        {/* Four Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,5fr)_minmax(0,3fr)] gap-3">
          {/* Column 1: Returned Items */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-red-700 flex items-center gap-2">
                  <ArrowLeft className="h-5 w-5" />
                  Returned
                </h2>
                <span className="text-sm text-gray-500">
                  Total: {formatPrice(totals.returnedTotal)}
                </span>
              </div>

              {/* Mode Toggle — hidden from UI; in-store mode is forced. Remove `hidden` to re-enable. */}
              <div className="hidden items-center gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setReturnMode('in-store')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    returnMode === 'in-store'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  In-Store
                </button>
                <button
                  type="button"
                  onClick={() => setReturnMode('web-order')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    returnMode === 'web-order'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Web Order
                </button>
              </div>

              {/* Search / Web Order UI */}
              <div className="mb-4">
                {returnMode === 'in-store' ? (
                  <ItemSearchComponent
                    placeholder="Search items to return..."
                    onItemSelect={handleReturnedItemSelect}
                    showNameSearch={false}
                    autoFocus={true}
                    disabled={saving}
                    showOutOfStock={true}
                    compact
                  />
                ) : (
                  <WebOrderLookup
                    fetchedOrderRefs={fetchedOrderRefs}
                    returnedItems={returnedItems}
                    disabled={saving}
                    onItemsFetched={(newItems) => {
                      setReturnedItems(prev => [...newItems, ...prev]);
                    }}
                    onOrderRefAdded={(ref) => {
                      setFetchedOrderRefs(prev => [...prev, ref]);
                    }}
                    onCustomerInfo={({ name, phone }) => {
                      if (!formData.CustomerName) {
                        setFormData(prev => ({
                          ...prev,
                          CustomerName: name,
                          CustomerPhone: phone || prev.CustomerPhone,
                        }));
                      }
                    }}
                  />
                )}
              </div>

              {/* Returned Items List */}
            <div className="min-h-[400px]">
              <ItemListComponent
                items={convertItemsForList(returnedItems)}
                itemQuantities={createItemQuantities(returnedItems)}
                onQuantityIncrement={handleReturnedItemQuantityIncrement}
                onQuantityDecrement={handleReturnedItemQuantityDecrement}
                onRemoveItem={(itemId) => removeItem(itemId, 'returned')}
                unitPrices={createEditableUnitPrices(returnedItems)}
                onPriceChange={handleReturnedItemPriceChange}
                emptyMessage="No returned items added yet"
                emptyDescription="Search and select items to return them."
                showStockStatus={returnMode === 'in-store'}
                showExpiryStatus={returnMode === 'in-store'}
                showDiscount={true}
                showPricePerUnit={true}
                showItemDetails={true}
                allowQuantityEdit={true}
                compact
              />
                    </div>
                  </div>

          {/* Column 2: Sold Items */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-green-700 flex items-center gap-2">
                  Sold
                  <ArrowRight className="h-5 w-5" />
                </h2>
                <span className="text-sm text-gray-500">
                  Total: {formatPrice(totals.givenTotal)}
                </span>
              </div>

              {/* Item Search */}
              <div className="mb-4">
              <ItemSearchComponent
                placeholder="Search items to give..."
                onItemSelect={handleGivenItemSelect}
                showNameSearch={false}
                disabled={saving}
                compact
              />
                    </div>

            {/* Sold Items List */}
            <div className="min-h-[400px]">
              <ItemListComponent
                items={convertItemsForList(givenItems)}
                itemQuantities={createItemQuantities(givenItems)}
                onQuantityIncrement={handleGivenItemQuantityIncrement}
                onQuantityDecrement={handleGivenItemQuantityDecrement}
                onRemoveItem={(itemId) => removeItem(itemId, 'given')}
                unitPrices={createEditableUnitPrices(givenItems)}
                onPriceChange={handleGivenItemPriceChange}
                emptyMessage="No Sold Items added yet"
                emptyDescription="Search and select items to give to the customer."
                showStockStatus={true}
                showExpiryStatus={true}
                showDiscount={true}
                showPricePerUnit={true}
                showItemDetails={true}
                allowQuantityEdit={true}
                compact
              />
                </div>
                      </div>

          {/* Column 3: Order Summary */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="h-4 w-4 text-gray-600" />
              <h2 className="text-sm font-semibold text-gray-900">Order Summary</h2>
            </div>

            <div className="space-y-3 text-sm">
              {/* Returned group */}
              <div className="rounded-lg border border-red-100 bg-red-50/40 p-3 space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-red-700 flex items-center gap-1">
                  <ArrowLeft className="h-3 w-3" />
                  Returned
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatPrice(totals.returnedTotal)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Discount %</span>
                  <div className="flex items-center gap-1">
                    <PercentageInput
                      value={totals.returnedItemsDiscountPercentage || 0}
                      onChange={handleReturnedItemsDiscountPercentageChange}
                      className="input-sm w-14 px-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      min={0}
                      max={100}
                      decimalPlaces={2}
                    />
                    {(totals.returnedDiscountAmount || 0) > 0 && (
                      <button
                        type="button"
                        onClick={clearReturnedItemsDiscount}
                        className="text-red-500 hover:text-red-700"
                        title="Clear returned items discount"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Discount Rs</span>
                  <CurrencyInput
                    value={round2(totals.returnedDiscountAmount || 0)}
                    onChange={handleReturnedItemsDiscountAmountChange}
                    className="input-sm w-24 px-2 text-right font-medium text-red-600 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                    min={0}
                    max={totals.returnedTotal}
                    title="Type the discount in rupees"
                  />
                </div>
                <div className="flex justify-between pt-1 border-t border-red-100">
                  <span className="font-semibold text-gray-900">Net</span>
                  <span className="font-semibold text-red-700">{formatPrice(totals.netReturnedTotal)}</span>
                </div>
              </div>

              {/* Given group */}
              <div className="rounded-lg border border-green-100 bg-green-50/40 p-3 space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-green-700 flex items-center gap-1">
                  Given
                  <ArrowRight className="h-3 w-3" />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatPrice(totals.givenTotal)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Discount %</span>
                  <div className="flex items-center gap-1">
                    <PercentageInput
                      value={totals.givenItemsDiscountPercentage || 0}
                      onChange={handleGivenItemsDiscountPercentageChange}
                      className="input-sm w-14 px-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      min={0}
                      max={100}
                      decimalPlaces={2}
                    />
                    {(totals.givenDiscountAmount || 0) > 0 && (
                      <button
                        type="button"
                        onClick={clearGivenItemsDiscount}
                        className="text-red-500 hover:text-red-700"
                        title="Clear given items discount"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Discount Rs</span>
                  <CurrencyInput
                    value={round2(totals.givenDiscountAmount || 0)}
                    onChange={handleGivenItemsDiscountAmountChange}
                    className="input-sm w-24 px-2 text-right font-medium text-green-600 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                    min={0}
                    max={totals.givenTotal}
                    title="Type the discount in rupees"
                  />
                </div>
                <div className="flex justify-between pt-1 border-t border-green-100">
                  <span className="font-semibold text-gray-900">Net</span>
                  <span className="font-semibold text-green-700">{formatPrice(totals.netGivenTotal)}</span>
                </div>
              </div>

              {/* Settlement */}
              {(() => {
                const diff = totals.exchangeDifference;
                if (diff > 0) {
                  return (
                    <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-1">Customer pays</div>
                      <div className="text-2xl font-bold text-red-700 break-words">{formatPrice(diff)}</div>
                    </div>
                  );
                }
                if (diff < 0) {
                  return (
                    <div className="rounded-lg border-2 border-green-300 bg-green-50 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-green-700 mb-1">Refund to customer</div>
                      <div className="text-2xl font-bold text-green-700 break-words">{formatPrice(Math.abs(diff))}</div>
                    </div>
                  );
                }
                return (
                  <div className="rounded-lg border-2 border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Settlement</div>
                    <div className="text-2xl font-bold text-gray-700">Even exchange</div>
                  </div>
                );
              })()}
            </div>
            
            {/* Remarks */}
            <div className="mt-4">
              <label htmlFor="exchange-remarks" className="block text-xs font-semibold text-gray-700 mb-1">
                Remarks
              </label>
              <textarea
                id="exchange-remarks"
                value={formData.ExchangeRemarks}
                onChange={(e) => setFormData(prev => ({ ...prev, ExchangeRemarks: e.target.value }))}
                rows={3}
                maxLength={255}
                placeholder="Original sale #, reason for the price change, etc."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Save Exchange Button */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleOpenCheckout}
                disabled={saving || (returnedItems.length === 0 && givenItems.length === 0)}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
                title="Save Exchange (Ctrl+Enter)"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Saving...' : 'Save'}</span>
                <span className="text-sm opacity-75 ml-2">(Ctrl+Enter)</span>
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Checkout Info Modal */}
      <CheckoutInfoModal
        isOpen={checkoutOpen}
        onClose={handleCloseCheckout}
        onConfirm={handleConfirmCheckout}
        netAmount={totals.exchangeDifference}
        cashReceived={cashReceived}
        onCashReceivedChange={setCashReceived}
        bankReceived={bankReceived}
        onBankReceivedChange={setBankReceived}
        title="Exchange Checkout"
        confirmButtonText="Complete Exchange"
        cancelButtonText="Cancel"
        showBankReceived={true}
        autoFocus={true}
        disabled={saving}
        checkoutType="exchange"
      />
    </div>
  );
};

export default ExchangeForm;
